import Accordion from '@app/components/common/Accordion';
import AccordionSection from '@app/components/common/Accordion/AccordionSection';
import Box from '@app/components/common/Box';
import ButtonLink from '@app/components/common/Button/ButtonLink';
import Card from '@app/components/common/Card';
import Checkbox from '@app/components/common/Checkbox';
import ColorPicker from '@app/components/common/ColorPicker';
import Divider from '@app/components/common/Divider';
import Dropdown from '@app/components/common/Dropdown';
import Grid from '@app/components/common/Grid';
import Input from '@app/components/common/Input';
import { labelStyles } from '@app/components/common/Input/styles';
import MoneyInput from '@app/components/common/MoneyInput';
import Switch from '@app/components/common/Switch';
import TimePicker from '@app/components/common/TimePicker/TimePicker';
import Typography from '@app/components/common/Typography';
import DetailsSubtitle from '@app/components/LoggedIn/DetailsSubtitle';
import BreadCrumbSettings from '@app/components/LoggedIn/Settings/BreadCrumbSettings';
import {
  getPathWithOrgData,
  openNewTabWithOrgData,
} from '@app/helpers/navigation';
import useNavigateWithOrg from '@app/hooks/useNavigateWithOrg';
import useRootSelector from '@app/hooks/useRootSelector';
import { actionCreatorsSettingsWeb } from '@app/state';
import {
  currentTaxesSettingsSelector,
  makeSelectSettingsData,
  selectRoles,
  settingsErrorsSelector,
} from '@app/state/selectors/settingsSelectors';
import {
  defaultTipInputToValue,
  splitTipIntervalToValue,
  tipDistributionToValue,
  tipPayoutMethodsToValue,
  tipShareByToValue,
  valueToDefaultTipInput,
  valueToSplitTipInterval,
  valueToTipDistribution,
  valueToTipPayoutMethods,
  valueToTipShareBy,
} from '@app/state/settings/paymentsFactory';
import { loadSettingsBucketChangeData as loadSettingsBucketChangeDataAction } from '@app/state/settings/settingsWebActions';
import { store, useAppDispatch } from '@app/state/store';
import { displayFlexEndColumnStyles } from '@app/theme/commonStyles';
import { MQ_MAX_MOBILE } from '@app/theme/types';
import { IGenericError } from '@app/types';
import { useTheme } from '@emotion/react';
import { bindActionCreators } from '@reduxjs/toolkit';
import {
  actionCreatorsSettings,
  actionCreatorsSettingsChangeData,
  generateTabletGeneratedId,
  IPaymentsSettings,
  isValidDateTime,
  ITipRolePool,
  ITipRolesPool,
  ITipShareRule,
  ITipSharingRules,
  ServiceChargeAssignTo,
  ServiceChargeDiscount,
  SettingsSectionId,
  SplitTipInterval,
  TipDistribution,
  TipShareBy,
} from '@westondev/tableturn-core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { WithTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import DualPricing from './DualPricing';
import ServiceCharge from './ServiceCharge';
import ServiceChargeForm from './ServiceCharge/ServiceChargeForm';
import TipRolePoolTable from './TipRolePoolTable';
import TipSharingRuleModal from './TipSharingRuleModal';
import TipSharingRulesTable from './TipSharingRulesTable';

const SECTION_ID = SettingsSectionId.PAYMENTS;
const getMemoizedItemData =
  makeSelectSettingsData<IPaymentsSettings>(SECTION_ID);

const gridItemSizing = {
  mb: 12,
  sm: 6,
  md: 6,
  lg: 4,
  xl: 3,
};

const tenderTypesGridSizing = {
  mb: 1,
  sm: 1,
  md: 1,
  lg: 1,
  xl: 1,
};

const tenderTypesTitleStyles = {
  display: 'flex',
  alignItems: 'center',
};

const tenderTypesStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const Payments = ({ t }: WithTranslation) => {
  const dispatch = useAppDispatch();
  const match = useMatch(getPathWithOrgData('/settings/payments'));
  const navigate = useNavigateWithOrg();
  const location = useLocation();
  const customTendersPath = getPathWithOrgData(
    '/settings/payments/custom-tenders',
  );
  const { updateGenericSettings: setValue } = bindActionCreators(
    actionCreatorsSettings,
    dispatch,
  );
  const { clearSettingsError } = bindActionCreators(
    actionCreatorsSettingsChangeData,
    dispatch,
  );

  const loadSettingsBucketChangeData = bindActionCreators(
    loadSettingsBucketChangeDataAction,
    dispatch,
  );

  const { checkForSettingsChangesAndNavigateWeb } = bindActionCreators(
    actionCreatorsSettingsWeb,
    dispatch,
  );

  const [selectedSharingRule, setSelectedSharingRule] = useState<
    number | string | null
  >(null);

  const [serviceChargeId, setServiceChargeId] = useState<
    number | string | null
  >(null);

  const roles = useSelector(selectRoles);
  const settingsTaxes = useSelector(currentTaxesSettingsSelector);

  const { data } = useSelector(getMemoizedItemData);
  const isSet = useRootSelector(
    state => state.settings.changeData.settingsSelected === 'payments',
  );
  const settingsErrors = useSelector(settingsErrorsSelector);

  const theme = useTheme();

  useEffect(() => {
    if (!isSet) loadSettingsBucketChangeData({ setting: 'payments' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSet]);

  const batchTimeError =
    (settingsErrors?.batchTime?.data as IGenericError)?.message || '';

  const eGiftCardMerchantIdError =
    (settingsErrors?.eGiftCardMerchantId?.data as IGenericError)?.message || '';

  const eGiftCardLocationIdError =
    (settingsErrors?.eGiftCardLocationId?.data as IGenericError)?.message || '';

  const rolePoolRecipientsData = useMemo(() => {
    if (!data?.rolePoolRecipients) return [];
    return Object.values(data?.rolePoolRecipients || {}).map(role => ({
      id: role.id,
      roleId: role.roleId,
      amount: role.amount,
      actions: '',
    }));
  }, [data?.rolePoolRecipients]);

  const rolePoolContributorsData = useMemo(() => {
    return Object.values(data?.rolePoolContributors || {}).map(role => ({
      id: role.id,
      roleId: role.roleId,
      amount: role.amount,
      actions: '',
    }));
  }, [data?.rolePoolContributors]);

  const formatSharingRulesRecipients = useCallback(
    (recipients: ITipRolesPool) => {
      const recipientsText: string[] = [];

      Object.values(recipients).forEach(role => {
        recipientsText.push(`${roles?.[role.roleId]?.name}: ${role.amount}%`);
      });

      return recipientsText.length === 0 ? '- - -' : recipientsText.join(' , ');
    },
    [roles],
  );

  const serviceChargeArray = useMemo(
    () =>
      Object.keys(data?.serviceCharges || {}).map(key => {
        const taxes = data.serviceCharges[key].taxIds
          .map(taxId => {
            const tax = settingsTaxes[taxId];
            return tax ? tax.name : '';
          })
          .join(', ');

        const discount =
          data.serviceCharges[key].discount ===
          ServiceChargeDiscount.PRE_DISCOUNT
            ? t(
                'settingsModule.paymentsSettings.serviceChargeSettings.preDiscount',
              )
            : t(
                'settingsModule.paymentsSettings.serviceChargeSettings.postDiscount',
              );

        const assignTo =
          data.serviceCharges[key].assignTo === ServiceChargeAssignTo.SERVER
            ? t('settingsModule.paymentsSettings.serviceChargeSettings.server')
            : t(
                'settingsModule.paymentsSettings.serviceChargeSettings.restaurant',
              );

        return {
          ...data.serviceCharges[key],
          id: Number(key),
          taxes,
          orderTypes: data.serviceCharges[key].orderTypes,
          discount,
          assignTo,
          actions: '',
        };
      }),

    [data, settingsTaxes, t],
  );

  const sharingRulesData = useMemo(() => {
    return Object.values(data?.sharingRules || {}).map(rule => ({
      id: rule.id,
      contributor: roles[rule.contributorId]?.name,
      contribution: rule.contribution,
      recipients: formatSharingRulesRecipients(rule.recipients),
      actions: '',
    }));
  }, [data?.sharingRules, roles, formatSharingRulesRecipients]);

  const updateValue = (
    field: keyof IPaymentsSettings,
    newValue: number | string | Partial<ITipRolePool> | Partial<ITipShareRule>,
    id?: number | string,
  ) => {
    const currentData = store.getState().settings.changeData
      .data as IPaymentsSettings;

    if (
      id &&
      (field === 'rolePoolContributors' ||
        field === 'rolePoolRecipients' ||
        field === 'sharingRules')
    ) {
      setValue<IPaymentsSettings>(
        {
          [field]: {
            ...currentData[field],
            [id]: {
              ...(currentData[field] as ITipRolesPool)[id],
              ...(newValue as Partial<ITipRolePool>),
            },
          },
        },
        SECTION_ID,
      );
      return;
    }

    setValue<IPaymentsSettings>(
      {
        ...currentData,
        [field]: newValue,
      },
      SECTION_ID,
    );
  };

  const deleteSharingRule = (id: number) => {
    const newData = { ...data?.sharingRules };
    delete newData[id];
    setValue(
      {
        sharingRules: newData,
      },
      SECTION_ID,
    );
  };

  const handleTipDistributionChange = (value: number) => {
    if (data?.tipDistribution === valueToTipDistribution[value]) return;
    const newFields: Partial<IPaymentsSettings> = {};
    if (
      data?.tipDistribution === TipDistribution.INDIVIDUAL &&
      valueToTipDistribution[value] !== TipDistribution.INDIVIDUAL
    ) {
      newFields.splitTipInterval = SplitTipInterval.SHIFT;
    } else if (
      data?.tipDistribution !== TipDistribution.INDIVIDUAL &&
      valueToTipDistribution[value] === TipDistribution.INDIVIDUAL
    ) {
      newFields.splitTipInterval = null;
    }

    if (valueToTipDistribution[value] === TipDistribution.SHARING) {
      newFields.shareBy = TipShareBy.ROLE;
      newFields.sharingRules = {};
    } else {
      newFields.shareBy = null;
      newFields.sharingRules = null;
    }

    if (valueToTipDistribution[value] === TipDistribution.POOLING) {
      newFields.rolePoolContributors = {};
      newFields.rolePoolRecipients = {};
    } else {
      newFields.rolePoolContributors = null;
      newFields.rolePoolRecipients = null;
    }

    setValue(
      {
        tipDistribution: valueToTipDistribution[value],
        ...newFields,
      },
      SECTION_ID,
    );
  };

  const addRolePool = (key: 'rolePoolContributors' | 'rolePoolRecipients') => {
    if (Object.keys(roles).length === 0) return;
    const currentData = store.getState().settings.changeData
      .data as IPaymentsSettings;
    const currentRoles = new Set(
      Object.values(currentData[key] as ITipRolesPool).map(role => role.roleId),
    );

    let newRoleId = 0;
    for (const roleId of Object.keys(roles)) {
      if (!currentRoles.has(Number(roleId))) {
        newRoleId = Number(roleId);
        break;
      }
    }
    const newId = generateTabletGeneratedId();

    setValue(
      {
        [key]: {
          ...currentData[key],
          [newId]: {
            id: newId,
            roleId: newRoleId,
            amount: 0,
          },
        },
      },
      SECTION_ID,
    );
  };

  const addSharingRule = () => {
    if (Object.keys(roles).length === 0) return;

    setSelectedSharingRule(0);
  };

  const deleteRolePool = (
    key: 'rolePoolContributors' | 'rolePoolRecipients',
    id: number,
  ) => {
    const currentData = store.getState().settings.changeData
      .data as IPaymentsSettings;
    const newData = { ...currentData[key] };
    delete newData[id];
    setValue(
      {
        [key]: newData,
      },
      SECTION_ID,
    );
  };

  const generateSharingRuleObject = () => {
    if (Object.keys(roles).length === 0) return null;

    const sharingRules = (data?.sharingRules as ITipSharingRules) || {};
    const currentRoles = new Set(
      Object.values(sharingRules).map(rule => rule.contributorId),
    );

    let newContributorId = 0;
    for (const id of Object.keys(roles)) {
      if (!currentRoles.has(Number(id))) {
        newContributorId = Number(id);
        break;
      }
    }

    const newId = generateTabletGeneratedId();
    return {
      id: newId,
      contributorId: newContributorId,
      contribution: 0,
      recipients: {},
    };
  };

  const serviceChargeName = useMemo(() => {
    if (!data || !serviceChargeId) {
      return '';
    }
    if (typeof serviceChargeId === 'string') {
      return 'Adding Service Charge';
    }

    if (data.serviceCharges[serviceChargeId]) {
      return data.serviceCharges[serviceChargeId].name;
    }

    return '';
  }, [data, serviceChargeId]);

  return (
    <>
      {!match ? (
        <BreadCrumbSettings
          title="Payments"
          currentPage={location.pathname
            .split('/')
            .at(-1)
            ?.split('-')
            .join(' ')}
          onClick={() => {
            checkForSettingsChangesAndNavigateWeb(
              () => navigate('/settings/payments'),
              () => null,
              navigate,
            );
          }}
          isSticky
        />
      ) : (
        <>
          <TipSharingRuleModal
            data={
              selectedSharingRule !== null
                ? (data?.sharingRules || {})[selectedSharingRule] ??
                  generateSharingRuleObject()
                : null
            }
            sharingRulesData={data?.sharingRules || {}}
            active={selectedSharingRule !== null}
            onCancel={() => setSelectedSharingRule(null)}
            onSuccess={() => setSelectedSharingRule(null)}
            updateValue={updateValue}
          />
          {serviceChargeId ? (
            <>
              <BreadCrumbSettings
                title="Payments"
                currentPage={serviceChargeName}
                onClick={() => setServiceChargeId(null)}
                isSticky
              />
              <ServiceChargeForm
                serviceChargeId={serviceChargeId}
                navigateBack={() => {
                  setServiceChargeId(null);
                }}
              />
            </>
          ) : (
            <Accordion>
              <AccordionSection
                isExpanded
                title={t('settingsModule.paymentsSettings.general.title')}
                description={t(
                  'settingsModule.paymentsSettings.general.description',
                )}
                hasError={Boolean(batchTimeError)}>
                <Grid
                  rowGap={10}
                  columnGap={20}
                  csx={{
                    width: '100%',
                  }}>
                  <Grid.Item
                    {...gridItemSizing}
                    csx={displayFlexEndColumnStyles}>
                    <Checkbox
                      label={t(
                        'settingsModule.paymentsSettings.general.confirmDrawerStartingBalance',
                      )}
                      checked={data?.confirmDrawerStartingBalance}
                      onChange={checked => {
                        setValue(
                          {
                            confirmDrawerStartingBalance: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>

                  <Grid.Item
                    {...gridItemSizing}
                    csx={displayFlexEndColumnStyles}>
                    <Checkbox
                      label={t(
                        'settingsModule.paymentsSettings.general.printChecksOnCloseOrder',
                      )}
                      checked={data?.printChecksOnCloseOrder}
                      onChange={checked => {
                        setValue(
                          {
                            printChecksOnCloseOrder: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item
                    {...gridItemSizing}
                    csx={displayFlexEndColumnStyles}>
                    <Checkbox
                      label={t(
                        'settingsModule.paymentsSettings.general.forceAllPaidOrdersToTipPending',
                      )}
                      checked={data?.forceAllPaidOrdersToTipPending}
                      onChange={checked => {
                        setValue(
                          {
                            forceAllPaidOrdersToTipPending: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item
                    {...gridItemSizing}
                    csx={displayFlexEndColumnStyles}>
                    <Checkbox
                      label={t(
                        'settingsModule.paymentsSettings.general.requireManualClosingPostTip',
                      )}
                      checked={data?.requireManualClosingPostTip}
                      onChange={checked => {
                        setValue(
                          {
                            requireManualClosingPostTip: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                </Grid>
                <Divider
                  csx={{
                    marginBlock: '20px',
                  }}
                />
                <DetailsSubtitle>
                  {t(
                    'settingsModule.paymentsSettings.general.preAuthOptions.title',
                  )}
                </DetailsSubtitle>
                <Grid
                  rowGap={10}
                  columnGap={20}
                  csx={{
                    width: '100%',
                  }}>
                  <Grid.Item
                    {...gridItemSizing}
                    csx={displayFlexEndColumnStyles}>
                    <MoneyInput
                      value={data?.defaultPreAuthAmount || 0}
                      label={t(
                        'settingsModule.paymentsSettings.general.preAuthOptions.defaultPreAuthAmount',
                      )}
                      suffix=""
                      precision={0}
                      onValueChange={value => {
                        setValue(
                          {
                            defaultPreAuthAmount: value,
                          },
                          SECTION_ID,
                        );
                      }}
                      maxValue={9999}
                    />
                  </Grid.Item>
                  <Grid.Item
                    {...gridItemSizing}
                    xl={4}
                    csx={[
                      displayFlexEndColumnStyles,
                      {
                        alignSelf: 'center',
                      },
                    ]}>
                    <Checkbox
                      label={t(
                        'settingsModule.paymentsSettings.general.preAuthOptions.dueExceedsPreAuth',
                      )}
                      checked={data?.dueExceedsPreAuth || false}
                      onChange={checked => {
                        setValue(
                          {
                            dueExceedsPreAuth: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                </Grid>
                <DetailsSubtitle textCsx={{ marginTop: '20px' }} noMargin>
                  {t(
                    'settingsModule.paymentsSettings.general.batchSettings.title',
                  )}
                </DetailsSubtitle>
                <Grid
                  rowGap={10}
                  columnGap={20}
                  csx={{
                    width: '100%',
                  }}>
                  <Grid.Item
                    {...gridItemSizing}
                    csx={[
                      displayFlexEndColumnStyles,
                      { paddingBottom: batchTimeError ? '20px' : '0px' },
                    ]}>
                    <Switch
                      label={t(
                        'settingsModule.paymentsSettings.general.batchSettings.automatedBatching',
                      )}
                      checked={data?.automatedBatching || false}
                      onChange={automatedBatching => {
                        const isValid = isValidDateTime(data?.batchTime || '');

                        if (!automatedBatching && !isValid) {
                          clearSettingsError('batchTime');
                          setValue(
                            { batchTime: '2023-12-25T04:00:00Z' },
                            SECTION_ID,
                          );
                        }
                        setValue({ automatedBatching }, SECTION_ID);
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item
                    {...gridItemSizing}
                    xl={4}
                    csx={displayFlexEndColumnStyles}>
                    <TimePicker
                      label={t(
                        'settingsModule.paymentsSettings.general.batchSettings.batchTime',
                      )}
                      disabled={!data?.automatedBatching}
                      utcValue={data?.batchTime}
                      onChange={newTime =>
                        setValue({ batchTime: newTime }, SECTION_ID)
                      }
                      error={Boolean(batchTimeError)}
                      caption={batchTimeError}
                      onFocus={() =>
                        batchTimeError && clearSettingsError('batchTime')
                      }
                    />
                  </Grid.Item>
                </Grid>
              </AccordionSection>
              <AccordionSection
                isExpanded
                title={t('settingsModule.paymentsSettings.tenderTypes.title')}
                description={t(
                  'settingsModule.paymentsSettings.tenderTypes.description',
                )}
                hasError={
                  Boolean(eGiftCardMerchantIdError) ||
                  Boolean(eGiftCardLocationIdError)
                }>
                <DetailsSubtitle textCsx={{ marginBottom: '20px' }} noMargin>
                  {t(
                    'settingsModule.paymentsSettings.tenderTypes.defaultTenders',
                  )}
                </DetailsSubtitle>
                <Grid
                  rowGap={20}
                  columnGap={50}
                  csx={{
                    overflowX: 'auto',
                    gridTemplateColumns: 'repeat(9, minmax(60px, 1fr))',
                  }}>
                  <Grid.Item {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t('settingsModule.paymentsSettings.tenderTypes.status')}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t('settingsModule.paymentsSettings.tenderTypes.name')}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item
                    csx={tenderTypesTitleStyles}
                    {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t(
                        'settingsModule.paymentsSettings.tenderTypes.allowTips',
                      )}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item
                    csx={tenderTypesTitleStyles}
                    {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t(
                        'settingsModule.paymentsSettings.tenderTypes.autoConfirmTip',
                      )}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item
                    csx={tenderTypesTitleStyles}
                    {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t(
                        'settingsModule.paymentsSettings.tenderTypes.printReceipt',
                      )}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item
                    csx={tenderTypesTitleStyles}
                    {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t(
                        'settingsModule.paymentsSettings.tenderTypes.printMerchantCopy',
                      )}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t(
                        'settingsModule.paymentsSettings.tenderTypes.printTipTotalLines',
                      )}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t(
                        'settingsModule.paymentsSettings.tenderTypes.backgroundColor',
                      )}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item {...tenderTypesGridSizing}>
                    <Typography variant="caption" align="center">
                      {t(
                        'settingsModule.paymentsSettings.tenderTypes.textColor',
                      )}
                    </Typography>
                  </Grid.Item>

                  {/* --------- CARD --------- */}
                  <Grid.Item
                    csx={[tenderTypesStyles]}
                    {...tenderTypesGridSizing}>
                    <Switch
                      info=""
                      checked={data?.acceptCreditCardPayments || false}
                      onChange={acceptCreditCardPayments =>
                        setValue({ acceptCreditCardPayments }, SECTION_ID)
                      }
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Typography
                      color={
                        !data?.acceptCreditCardPayments
                          ? 'persistentSemanticGrey'
                          : undefined
                      }>
                      {t('settingsModule.paymentsSettings.tenderTypes.card')}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptCreditCardPayments}
                      checked={data?.creditCardAllowTips || false}
                      onChange={checked => {
                        setValue(
                          {
                            creditCardAllowTips: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptCreditCardPayments}
                      checked={data?.creditCardAutoConfirmTip || false}
                      onChange={checked => {
                        setValue(
                          {
                            creditCardAutoConfirmTip: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptCreditCardPayments}
                      checked={data?.creditCardPrintReceipt || false}
                      onChange={checked => {
                        setValue(
                          {
                            creditCardPrintReceipt: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptCreditCardPayments}
                      checked={data?.creditCardPrintMerchantCopy || false}
                      onChange={checked => {
                        setValue(
                          {
                            creditCardPrintMerchantCopy: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptCreditCardPayments}
                      checked={data?.creditCardPrintTip || false}
                      onChange={checked => {
                        setValue(
                          {
                            creditCardPrintTip: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <ColorPicker
                      isDisabled={!data?.acceptCreditCardPayments}
                      color={
                        data?.creditCardBackgroundColor || theme.colors.cardGrey
                      }
                      onChange={color => {
                        setValue(
                          {
                            creditCardBackgroundColor: color,
                          },
                          SECTION_ID,
                        );
                      }}
                      containerProps={{ width: '100%' }}
                      colorPickerProps={{ minWidth: '100%' }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <ColorPicker
                      isDisabled={!data?.acceptCreditCardPayments}
                      color={
                        data?.creditCardTextColor || theme.colors.textWhite
                      }
                      onChange={color => {
                        setValue(
                          {
                            creditCardTextColor: color,
                          },
                          SECTION_ID,
                        );
                      }}
                      containerProps={{ width: '100%' }}
                      colorPickerProps={{ minWidth: '100%' }}
                    />
                  </Grid.Item>

                  {/* --------- CASH --------- */}
                  <Grid.Item
                    csx={[tenderTypesStyles]}
                    {...tenderTypesGridSizing}>
                    <Switch
                      info=""
                      checked={data?.acceptCashPayments || false}
                      onChange={acceptCashPayments =>
                        setValue({ acceptCashPayments }, SECTION_ID)
                      }
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Typography
                      color={
                        !data?.acceptCashPayments
                          ? 'persistentSemanticGrey'
                          : undefined
                      }>
                      {t('settingsModule.paymentsSettings.tenderTypes.cash')}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={true}
                      checked={false}
                      onChange={() => null}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={true}
                      checked={false}
                      onChange={() => null}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptCashPayments}
                      checked={data?.cashPrintReceipt || false}
                      onChange={checked => {
                        setValue(
                          {
                            cashPrintReceipt: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptCashPayments}
                      checked={data?.cashPrintMerchantCopy || false}
                      onChange={checked => {
                        setValue(
                          {
                            cashPrintMerchantCopy: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={true}
                      checked={false}
                      onChange={() => null}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <ColorPicker
                      isDisabled={!data?.acceptCashPayments}
                      color={data?.cashBackgroundColor || theme.colors.cardGrey}
                      onChange={color => {
                        setValue(
                          {
                            cashBackgroundColor: color,
                          },
                          SECTION_ID,
                        );
                      }}
                      containerProps={{ width: '100%' }}
                      colorPickerProps={{ minWidth: '100%' }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <ColorPicker
                      isDisabled={!data?.acceptCashPayments}
                      color={data?.cashTextColor || theme.colors.textWhite}
                      onChange={color => {
                        setValue(
                          {
                            cashTextColor: color,
                          },
                          SECTION_ID,
                        );
                      }}
                      containerProps={{ width: '100%' }}
                      colorPickerProps={{ minWidth: '100%' }}
                    />
                  </Grid.Item>

                  {/* --------- HOUSE --------- */}
                  <Grid.Item
                    csx={[tenderTypesStyles]}
                    {...tenderTypesGridSizing}>
                    <Switch
                      info=""
                      checked={data?.acceptHousePayments || false}
                      onChange={acceptHousePayments =>
                        setValue({ acceptHousePayments }, SECTION_ID)
                      }
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Typography
                      color={
                        !data?.acceptHousePayments
                          ? 'persistentSemanticGrey'
                          : undefined
                      }>
                      {t('settingsModule.paymentsSettings.tenderTypes.house')}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptHousePayments}
                      checked={data?.houseAllowTips || false}
                      onChange={checked => {
                        setValue(
                          {
                            houseAllowTips: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptHousePayments}
                      checked={data?.houseAutoConfirmTip || false}
                      onChange={checked => {
                        setValue(
                          {
                            houseAutoConfirmTip: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptHousePayments}
                      checked={data?.housePrintReceipt || false}
                      onChange={checked => {
                        setValue(
                          {
                            housePrintReceipt: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptHousePayments}
                      checked={data?.housePrintMerchantCopy || false}
                      onChange={checked => {
                        setValue(
                          {
                            housePrintMerchantCopy: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.acceptHousePayments}
                      checked={data?.housePrintTip || false}
                      onChange={checked => {
                        setValue(
                          {
                            housePrintTip: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <ColorPicker
                      isDisabled={!data?.acceptHousePayments}
                      color={
                        data?.houseBackgroundColor || theme.colors.cardGrey
                      }
                      onChange={color => {
                        setValue(
                          {
                            houseBackgroundColor: color,
                          },
                          SECTION_ID,
                        );
                      }}
                      containerProps={{ width: '100%' }}
                      colorPickerProps={{ minWidth: '100%' }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <ColorPicker
                      isDisabled={!data?.acceptHousePayments}
                      color={data?.houseTextColor || theme.colors.textWhite}
                      onChange={color => {
                        setValue(
                          {
                            houseTextColor: color,
                          },
                          SECTION_ID,
                        );
                      }}
                      containerProps={{ width: '100%' }}
                      colorPickerProps={{ minWidth: '100%' }}
                    />
                  </Grid.Item>

                  {/* --------- GIFT CARD --------- */}
                  <Grid.Item
                    csx={[tenderTypesStyles]}
                    {...tenderTypesGridSizing}>
                    <Switch
                      info=""
                      checked={data?.enableGiftCardPayments || false}
                      onChange={enableGiftCardPayments =>
                        setValue({ enableGiftCardPayments }, SECTION_ID)
                      }
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Typography
                      color={
                        !data?.enableGiftCardPayments
                          ? 'persistentSemanticGrey'
                          : undefined
                      }>
                      {t(
                        'settingsModule.paymentsSettings.tenderTypes.giftCard',
                      )}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.enableGiftCardPayments}
                      checked={data?.giftCardAllowTips || false}
                      onChange={checked => {
                        setValue(
                          {
                            giftCardAllowTips: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.enableGiftCardPayments}
                      checked={data?.giftCardAutoConfirmTip || false}
                      onChange={checked => {
                        setValue(
                          {
                            giftCardAutoConfirmTip: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.enableGiftCardPayments}
                      checked={data?.giftCardPrintReceipt || false}
                      onChange={checked => {
                        setValue(
                          {
                            giftCardPrintReceipt: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.enableGiftCardPayments}
                      checked={data?.giftCardPrintMerchantCopy || false}
                      onChange={checked => {
                        setValue(
                          {
                            giftCardPrintMerchantCopy: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <Checkbox
                      isDisabled={!data?.enableGiftCardPayments}
                      checked={data?.giftCardPrintTip || false}
                      onChange={checked => {
                        setValue(
                          {
                            giftCardPrintTip: checked,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <ColorPicker
                      isDisabled={!data?.enableGiftCardPayments}
                      color={
                        data?.giftCardBackgroundColor || theme.colors.cardGrey
                      }
                      onChange={color => {
                        setValue(
                          {
                            giftCardBackgroundColor: color,
                          },
                          SECTION_ID,
                        );
                      }}
                      containerProps={{ width: '100%' }}
                      colorPickerProps={{ minWidth: '100%' }}
                    />
                  </Grid.Item>
                  <Grid.Item csx={tenderTypesStyles} {...tenderTypesGridSizing}>
                    <ColorPicker
                      isDisabled={!data?.enableGiftCardPayments}
                      color={data?.giftCardTextColor || theme.colors.textWhite}
                      onChange={color => {
                        setValue(
                          {
                            giftCardTextColor: color,
                          },
                          SECTION_ID,
                        );
                      }}
                      containerProps={{ width: '100%' }}
                      colorPickerProps={{ minWidth: '100%' }}
                    />
                  </Grid.Item>
                </Grid>

                <DetailsSubtitle
                  textCsx={{ marginBottom: '5px', marginTop: '25px' }}
                  noMargin>
                  {t(
                    'settingsModule.paymentsSettings.tenderTypes.eGiftIntegration',
                  )}
                </DetailsSubtitle>
                <Grid
                  rowGap={10}
                  columnGap={20}
                  csx={{
                    width: '100%',
                  }}>
                  <Grid.Item {...gridItemSizing}>
                    <label
                      css={[
                        labelStyles,
                        { [MQ_MAX_MOBILE]: { display: 'none' } },
                      ]}>
                      &nbsp;
                    </label>
                    <Switch
                      label={t(
                        'settingsModule.paymentsSettings.tenderTypes.enableEGiftIntegration',
                      )}
                      checked={data?.enableEGiftIntegration || false}
                      isDisabled={!data?.enableGiftCardPayments}
                      onChange={enableEGiftIntegration =>
                        setValue({ enableEGiftIntegration }, SECTION_ID)
                      }
                    />
                  </Grid.Item>
                  <Grid.Item {...gridItemSizing}>
                    <Input
                      label={t(
                        'settingsModule.paymentsSettings.tenderTypes.eGiftCardMerchantId',
                      )}
                      placeholder={t(
                        'settingsModule.paymentsSettings.tenderTypes.eGiftCardMerchantId',
                      )}
                      disabled={
                        !data?.enableEGiftIntegration ||
                        !data?.enableGiftCardPayments
                      }
                      value={data?.eGiftCardMerchantId || ''}
                      required
                      onFocus={() =>
                        eGiftCardMerchantIdError &&
                        clearSettingsError('eGiftCardMerchantId')
                      }
                      error={Boolean(eGiftCardMerchantIdError)}
                      caption={eGiftCardMerchantIdError}
                      onChange={e => {
                        const value = e.target.value;
                        if (value.length > 7) return;
                        setValue(
                          {
                            eGiftCardMerchantId:
                              value.length === 0 ? null : value,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item {...gridItemSizing}>
                    <Input
                      label={t(
                        'settingsModule.paymentsSettings.tenderTypes.eGiftCardLocationId',
                      )}
                      placeholder={t(
                        'settingsModule.paymentsSettings.tenderTypes.eGiftCardLocationId',
                      )}
                      disabled={
                        !data?.enableEGiftIntegration ||
                        !data?.enableGiftCardPayments
                      }
                      value={data?.eGiftCardLocationId || ''}
                      required
                      onFocus={() =>
                        eGiftCardLocationIdError &&
                        clearSettingsError('eGiftCardLocationId')
                      }
                      error={Boolean(eGiftCardLocationIdError)}
                      caption={eGiftCardLocationIdError}
                      onChange={e => {
                        const value = e.target.value;
                        if (value.length > 4) return;
                        setValue(
                          {
                            eGiftCardLocationId:
                              value.length === 0 ? null : value,
                          },
                          SECTION_ID,
                        );
                      }}
                    />
                  </Grid.Item>
                </Grid>
              </AccordionSection>
              <AccordionSection
                title={t(
                  'settingsModule.paymentsSettings.drawerSettings.title',
                )}
                description={t(
                  'settingsModule.paymentsSettings.drawerSettings.description',
                )}></AccordionSection>
              <AccordionSection
                title={t('settingsModule.paymentsSettings.tipSettings.title')}>
                <Box
                  csx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                  }}>
                  <Grid
                    rowGap={20}
                    columnGap={20}
                    csx={{ alignItems: 'flex-start' }}>
                    <Grid.Item
                      {...gridItemSizing}
                      csx={displayFlexEndColumnStyles}>
                      <Dropdown
                        label={t(
                          'settingsModule.paymentsSettings.tipSettings.tipPayoutMethod',
                        )}
                        data={[
                          [
                            {
                              label: t(
                                'settingsModule.paymentsSettings.tipSettings.manual',
                              ),
                              value: 0,
                            },
                            {
                              label: t(
                                'settingsModule.paymentsSettings.tipSettings.payroll',
                              ),
                              value: 1,
                            },
                          ],
                        ]}
                        value={
                          tipPayoutMethodsToValue[data?.tipPayoutMethod] || 0
                        }
                        onChange={value => {
                          updateValue(
                            'tipPayoutMethod',
                            valueToTipPayoutMethods[value],
                          );
                        }}
                        isDisabled
                        placeholder=""
                      />
                    </Grid.Item>
                    <Grid.Item
                      {...gridItemSizing}
                      csx={displayFlexEndColumnStyles}>
                      <Dropdown
                        label={t(
                          'settingsModule.paymentsSettings.tipSettings.tipDistribution',
                        )}
                        data={[
                          [
                            {
                              label: t(
                                'settingsModule.paymentsSettings.tipSettings.individual',
                              ),
                              value: 0,
                            },
                            {
                              label: t(
                                'settingsModule.paymentsSettings.tipSettings.pooling',
                              ),
                              value: 1,
                            },
                            {
                              label: t(
                                'settingsModule.paymentsSettings.tipSettings.sharing',
                              ),
                              value: 2,
                            },
                          ],
                        ]}
                        value={
                          tipDistributionToValue[data?.tipDistribution] || 0
                        }
                        onChange={value => handleTipDistributionChange(value)}
                        placeholder=""
                      />
                    </Grid.Item>
                    <Grid.Item
                      {...gridItemSizing}
                      csx={displayFlexEndColumnStyles}>
                      <Dropdown
                        label={t(
                          'settingsModule.paymentsSettings.tipSettings.defaultTipInput',
                        )}
                        data={[
                          [
                            {
                              label: t(
                                'settingsModule.paymentsSettings.tipSettings.tipAmount',
                              ),
                              value: 0,
                            },
                            {
                              label: t(
                                'settingsModule.paymentsSettings.tipSettings.newTotal',
                              ),
                              value: 1,
                            },
                          ],
                        ]}
                        value={
                          defaultTipInputToValue[data?.defaultTipInput] || 0
                        }
                        onChange={value =>
                          setValue(
                            {
                              defaultTipInput: valueToDefaultTipInput[value],
                            },
                            SECTION_ID,
                          )
                        }
                      />
                    </Grid.Item>
                  </Grid>
                  {data?.tipDistribution === TipDistribution.POOLING && (
                    <>
                      <Divider />
                      <DetailsSubtitle noMargin>
                        {t(
                          'settingsModule.paymentsSettings.tipSettings.poolingRules',
                        )}
                      </DetailsSubtitle>

                      <TipRolePoolTable
                        title={t(
                          'settingsModule.paymentsSettings.tipSettings.rolesContributingToPool',
                        )}
                        firstColumnTitle={t(
                          'settingsModule.paymentsSettings.tipSettings.contributor',
                        )}
                        secondColumnTitle={t(
                          'settingsModule.paymentsSettings.tipSettings.percentageContributed',
                        )}
                        hasZeroText={t(
                          'settingsModule.paymentsSettings.tipSettings.percentageContributedRequired',
                        )}
                        data={rolePoolContributorsData}
                        updateValue={(
                          newValue: Partial<ITipRolePool>,
                          id: number | string,
                        ) => updateValue('rolePoolContributors', newValue, id)}
                        newRowText={t(
                          'settingsModule.paymentsSettings.tipSettings.addContributor',
                        )}
                        onNewRow={() => addRolePool('rolePoolContributors')}
                        onRowDelete={(id: number | string) =>
                          deleteRolePool('rolePoolContributors', id as number)
                        }
                        noDataText={t(
                          'settingsModule.paymentsSettings.tipSettings.emptyContributorsPool',
                        )}
                        customErrorText={
                          Object.keys(data.rolePoolContributors || {})
                            .length === 0 &&
                          Object.keys(data.rolePoolRecipients || {}).length > 0
                            ? t(
                                'settingsModule.paymentsSettings.tipSettings.noContributorsError',
                              )
                            : undefined
                        }
                      />

                      <Divider />

                      <TipRolePoolTable
                        validatePercentages
                        title={t(
                          'settingsModule.paymentsSettings.tipSettings.rolesReceivingFromPool',
                        )}
                        firstColumnTitle={t(
                          'settingsModule.paymentsSettings.tipSettings.recipient',
                        )}
                        secondColumnTitle={t(
                          'settingsModule.paymentsSettings.tipSettings.percentageReceived',
                        )}
                        hasZeroText={t(
                          'settingsModule.paymentsSettings.tipSettings.percentageReceivedRequired',
                        )}
                        data={rolePoolRecipientsData}
                        updateValue={(
                          newValue: Partial<ITipRolePool>,
                          id: number | string,
                        ) => updateValue('rolePoolRecipients', newValue, id)}
                        newRowText={t(
                          'settingsModule.paymentsSettings.tipSettings.addRecipients',
                        )}
                        onNewRow={() => addRolePool('rolePoolRecipients')}
                        onRowDelete={(id: number | string) =>
                          deleteRolePool('rolePoolRecipients', id as number)
                        }
                        noDataText={t(
                          'settingsModule.paymentsSettings.tipSettings.emptyRecipientsPool',
                        )}
                        customErrorText={
                          Object.keys(data?.rolePoolContributors || {}).length >
                            0 &&
                          Object.keys(data?.rolePoolRecipients || {}).length ===
                            0
                            ? t(
                                'settingsModule.paymentsSettings.tipSettings.noRecipientsError',
                              )
                            : undefined
                        }
                      />
                    </>
                  )}
                  {data?.tipDistribution === TipDistribution.SHARING &&
                    data.shareBy && (
                      <>
                        <Divider />
                        <DetailsSubtitle noMargin>
                          {t(
                            'settingsModule.paymentsSettings.tipSettings.sharingRules',
                          )}
                        </DetailsSubtitle>

                        <Grid
                          rowGap={20}
                          columnGap={20}
                          csx={{ alignItems: 'flex-start' }}>
                          <Grid.Item
                            {...gridItemSizing}
                            csx={displayFlexEndColumnStyles}>
                            <Dropdown
                              label={t(
                                'settingsModule.paymentsSettings.tipSettings.shareBy',
                              )}
                              data={[
                                [
                                  {
                                    label: t(
                                      'settingsModule.paymentsSettings.tipSettings.role',
                                    ),
                                    value: 0,
                                  },
                                ],
                              ]}
                              value={tipShareByToValue[data.shareBy]}
                              onChange={value => {
                                updateValue(
                                  'shareBy',
                                  valueToTipShareBy[value],
                                );
                              }}
                            />
                          </Grid.Item>
                        </Grid>

                        <TipSharingRulesTable
                          data={sharingRulesData}
                          onNewRow={addSharingRule}
                          onRowDelete={id => deleteSharingRule(id)}
                          onRowEdit={(id: number) => setSelectedSharingRule(id)}
                        />
                      </>
                    )}
                  {data?.tipDistribution !== TipDistribution.INDIVIDUAL &&
                    data?.splitTipInterval && (
                      <>
                        <Divider />
                        <Box>
                          <DetailsSubtitle noMargin>
                            {t(
                              'settingsModule.paymentsSettings.tipSettings.splittingRules',
                            )}
                          </DetailsSubtitle>
                          <Grid
                            rowGap={20}
                            columnGap={20}
                            csx={{ alignItems: 'flex-start' }}>
                            <Grid.Item
                              {...gridItemSizing}
                              csx={displayFlexEndColumnStyles}>
                              <Dropdown
                                label={t(
                                  'settingsModule.paymentsSettings.tipSettings.interval',
                                )}
                                data={[
                                  [
                                    {
                                      label: t(
                                        'settingsModule.paymentsSettings.tipSettings.shift',
                                      ),
                                      value: 0,
                                    },
                                    {
                                      label: t(
                                        'settingsModule.paymentsSettings.tipSettings.day',
                                      ),
                                      value: 1,
                                    },
                                  ],
                                ]}
                                value={
                                  splitTipIntervalToValue[
                                    data?.splitTipInterval || 0
                                  ]
                                }
                                onChange={value => {
                                  updateValue(
                                    'splitTipInterval',
                                    valueToSplitTipInterval[value],
                                  );
                                }}
                                placeholder=""
                              />
                            </Grid.Item>
                          </Grid>
                        </Box>
                      </>
                    )}
                </Box>
              </AccordionSection>
              <AccordionSection
                title={t(
                  'settingsModule.paymentsSettings.serviceChargeSettings.title',
                )}>
                <ServiceCharge
                  data={serviceChargeArray}
                  navigateToServiceCharge={id => setServiceChargeId(id)}
                />
              </AccordionSection>
              <DualPricing />
              <Card.SubCard
                title={t(
                  'settingsModule.paymentsSettings.customTenders.title',
                )}>
                <Grid columnGap={20} rowGap={15} csx={{ alignItems: 'center' }}>
                  <Grid.Item mb={12} sm={6} lg={8}>
                    <Typography>
                      {t(
                        'settingsModule.paymentsSettings.customTenders.navigationBox.description',
                      )}
                    </Typography>
                  </Grid.Item>
                  <Grid.Item mb={12} sm={6} lg={4}>
                    <ButtonLink
                      to={customTendersPath}
                      variant="primary"
                      onClick={openInNewTab => {
                        if (openInNewTab)
                          return openNewTabWithOrgData(customTendersPath);

                        navigate(customTendersPath, { state: location.state });
                      }}>
                      {t(
                        'settingsModule.paymentsSettings.customTenders.navigationBox.button',
                      )}
                    </ButtonLink>
                  </Grid.Item>
                </Grid>
              </Card.SubCard>
            </Accordion>
          )}
        </>
      )}
      <Outlet />
    </>
  );
};

export default Payments;
